// chart-race Remotion composition：柱状图竞速（专业版）。
// 复用 ./tick 的 chartRaceInterpolate 推导当前帧（value 插值 + 排序）。
// 排名切换平滑：预算每根柱子的"排名随帧"阶梯时间线（useMemo，仅 data 变时算），
// 渲染时在切换点附近用 smoothstep 插值 Y → 柱子顺滑滑入新名次、互换时优雅交叉，
// 而非瞬间跳位。frame 0 即完整可见。
import { useMemo } from 'react'
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion'
import { chartRaceInterpolate } from './tick'
import type { ChartRaceData } from './types'
import type { CompositionProps } from '../../remotion/ScenePlayer'
import { THEME } from '../../remotion/theme'

const ROW_H = 110
const BAR_H = 84
const PAD_X = 48
const LABEL_W = 300
const SMOOTH_FRAMES = 14 // 排名切换的过渡帧数（~0.47s @30fps）

type Pt = { f: number; rank: number }

/** 采样全时长，记录每个 label 的排名变化点（阶梯时间线）。 */
function buildRankTimelines(d: ChartRaceData, duration: number, fps: number): Record<string, Pt[]> {
  const total = Math.max(1, Math.round(duration * fps))
  const tl: Record<string, Pt[]> = {}
  const last: Record<string, number> = {}
  for (let f = 0; f <= total; f++) {
    const { items } = chartRaceInterpolate(d as any, f / fps, duration)
    items.forEach((it, rank) => {
      if (last[it.label] !== rank) {
        ;(tl[it.label] ??= []).push({ f, rank })
        last[it.label] = rank
      }
    })
  }
  return tl
}

/** 给定时间线 + 当前帧，返回平滑后的（小数）排名。 */
function smoothRank(timeline: Pt[] | undefined, frame: number): number {
  if (!timeline || timeline.length === 0) return 0
  let i = 0
  for (let k = 0; k < timeline.length; k++) {
    if (timeline[k].f <= frame) i = k
    else break
  }
  const cur = timeline[i]
  if (i === 0) return cur.rank // 首段稳定（frame 0 可见）
  const prev = timeline[i - 1]
  const t = (frame - cur.f) / SMOOTH_FRAMES
  if (t >= 1) return cur.rank
  const tt = t * t * (3 - 2 * t) // smoothstep（easeInOut）
  return prev.rank + (cur.rank - prev.rank) * tt
}

export const ChartRaceComposition: React.FC<CompositionProps> = ({ data, duration }) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const d = data as unknown as ChartRaceData
  const time = frame / fps
  const { year, items } = chartRaceInterpolate(data, time, duration)
  const timelines = useMemo(() => buildRankTimelines(d, duration, fps), [d, duration, fps])

  if (items.length === 0) return <AbsoluteFill style={{ background: THEME.bg }} />

  const maxVal = items[0]?.value || 1
  const digits = d.valueDigits ?? 2
  const prefix = d.valuePrefix ?? ''
  const suffix = d.valueSuffix ?? ''
  const fmt = (v: number) => `${prefix}${v.toFixed(digits)}${suffix}`

  const title = d.title ?? ''
  const n = items.length
  const blockH = n * ROW_H
  const yearH = 150
  const titleH = title ? 200 : 0
  const top0 = Math.max(120, (THEME.H - titleH - yearH - blockH) / 2)

  return (
    <AbsoluteFill style={{ background: (data as any).bgColor || THEME.bg, fontFamily: THEME.fontSans }}>
      {title && (
        <div style={{
          position: 'absolute', top: top0, left: PAD_X, right: PAD_X,
          color: THEME.w, fontWeight: 800, fontSize: 76, lineHeight: 1.15,
        }}>
          {title}
        </div>
      )}
      <div style={{
        position: 'absolute', top: top0 + titleH, left: PAD_X,
        color: THEME.pHover, fontWeight: 800, fontSize: 96, fontFamily: THEME.fontMono,
      }}>
        {year}
      </div>
      <div style={{ position: 'absolute', top: top0 + titleH + yearH, left: 0, right: 0 }}>
        {items.map((it) => {
          const w = Math.max(2, (it.value / maxVal) * (THEME.W - PAD_X * 2 - LABEL_W))
          const x = PAD_X + LABEL_W
          const y = smoothRank(timelines[it.label], frame) * ROW_H
          return (
            <div key={it.label} style={{ position: 'absolute', top: y, left: 0, right: 0, height: BAR_H }}>
              <span style={{
                position: 'absolute', left: PAD_X, top: 0, width: LABEL_W - 24, height: BAR_H,
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                color: THEME.t2, fontSize: 44, fontWeight: 600, textAlign: 'right',
              }}>
                {it.label}
              </span>
              <div style={{
                position: 'absolute', left: x, top: 0, height: BAR_H,
                width: THEME.W - PAD_X - x, background: 'rgba(255,255,255,0.05)', borderRadius: 18,
              }} />
              <div style={{
                position: 'absolute', left: x, top: 0, height: BAR_H, width: w, borderRadius: 18,
                background: `linear-gradient(90deg, ${it.color}, ${it.color}cc)`,
                boxShadow: `0 8px 24px ${it.color}40`,
                display: 'flex', alignItems: 'center',
              }}>
                <span style={{ paddingLeft: 26, color: '#fff', fontSize: 42, fontWeight: 800 }}>
                  {fmt(it.value)}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </AbsoluteFill>
  )
}
